import { Alerts } from "../../api/homeSiteAlerts/types";
import { Exceptions } from "../../api/homeSiteExceptions/types";
import { MilestoneTask } from "../../api/homeSiteMilestones/types";
import { Notes } from "../../api/homeSiteNotes/types";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isBetween from "dayjs/plugin/isBetween";
import dayjs from "dayjs";
import { AlignmentType, ALL_MILESTONES, FUTURE_WEEKS, NEXT_WEEK, NO_DUE_DATE, THIS_WEEK } from "../../pages/closings/dashboard/taskMetricsComponent";
import { ALERT_ACTIVE_STATUS } from "../constants";
import { DivisionMileStone, DivisionMileStones } from "../../api/divisionMileStones/types";
import { HomeSitesDataIndex } from "../../reactQuery/homeSitesDataIndex/types";

dayjs.extend(isSameOrBefore);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

export type MileStoneCount = {
  numHomesites: number;
  numOverdue: number;
}

export type MilestoneMetrics = {
  thisWeek: Record<string, MilestoneTask[]>; // MilestoneName -> MilestoneTask[]
  nextWeek: Record<string, MilestoneTask[]>; // MilestoneName -> MilestoneTask[]
  futureWeek: Record<string, MilestoneTask[]>; // MilestoneName -> MilestoneTask[]
  thisWeekOverDue: Record<string, MilestoneTask[]>; // MilestoneName -> MilestoneTask[]
  nextWeekOverDue: Record<string, MilestoneTask[]>; // MilestoneName -> MilestoneTask[]
  noDueDate: Map<string, Map<string, MilestoneTask>>; // HomeSiteId -> MilestoneName -> MilestoneTask
  noDueDateCount: number;
};

export type ITaskHomesite = HomeSitesDataIndex & {
  homesiteId: string;
  mileStoneName: string;
  taskName: string;
  dueDate: Date | null;
  id: string;
  concierge: string;
  numNotes: number;
  numExceptions: number;
  numAlerts: number;
};

export const getHomeSiteTaskMetrics = (divTasks: DivisionMileStones, tasks: MilestoneTask[], filteredData: HomeSitesDataIndex[], msLoading: boolean): MilestoneMetrics => {
  if (msLoading) {
    return {
      thisWeek: {},
      nextWeek: {},
      futureWeek: {},
      thisWeekOverDue: {},
      nextWeekOverDue: {},
      noDueDate: new Map(),
      noDueDateCount: 0,
    };
  };

  const today = dayjs().startOf("day");
  const startOfThisWeek = today.startOf("week").add(1, "day"); // Monday
  const endOfThisWeek = startOfThisWeek.add(6, "day").endOf("day"); // Sunday
  const startOfNextWeek = startOfThisWeek.add(1, "week"); // Next Monday
  const endOfNextWeek = startOfNextWeek.add(6, "day").endOf("day"); // Next Sunday

  // Optimize filteredHomeSiteIds creation - single pass
  const filteredHomeSiteIds = new Set<string>();
  for (const hs of filteredData) {
    if ((!hs.closeOfEscrowActual || hs.closeOfEscrowActual === null) && hs.saleDate != null) {
      filteredHomeSiteIds.add(hs.id);
    }
  }
  
  // Filter division milestones to only include active ones (status = true)
  const activeDivisionMilestones = divTasks.data.filter(milestone => milestone.status === true && milestone.automated !== true);
  const activeMilestoneNames = new Set(activeDivisionMilestones.map(milestone => milestone.mileStoneName));
  
  // pre filter tasks so fewer to go through - only include tasks for active milestones
  const filteredTasks = tasks.filter((task) => {
    const isActiveMilestone = activeMilestoneNames.has(task.mileStoneName);
    const isActiveTask = !(task.completionDate || task.status === false) && task.dueDate != null;
    return isActiveMilestone && isActiveTask;
  });

  const taskOrder: Record<string, number> = {};
  let idx = 0;
  for (const milestone of activeDivisionMilestones) {
    taskOrder[milestone.mileStoneName + milestone.taskName] = idx++;
  }

  // Single pass through filteredTasks to categorize all tasks at once
  const { thisWeekTasks, nextWeekTasks, futureWeekTasks, overDueThisWeekTasks } = categorizeTasksInSinglePass(
    filteredTasks,
    filteredHomeSiteIds,
    taskOrder,
    today,
    startOfThisWeek,
    endOfThisWeek,
    startOfNextWeek,
    endOfNextWeek
  );
  
  const noDueDateTasks = noTaskDueHomeSites(divTasks, tasks, filteredHomeSiteIds, activeMilestoneNames);
  const noDueDateCount = Array.from(noDueDateTasks.values()).reduce((acc, curr) => acc + curr.size, 0);
  const overDueNextWeekTasks = [] as MilestoneTask[];

  return {
    thisWeek: groupByMilestoneName(thisWeekTasks),
    nextWeek: groupByMilestoneName(nextWeekTasks),
    futureWeek: groupByMilestoneName(futureWeekTasks),
    thisWeekOverDue: groupByMilestoneName(overDueThisWeekTasks),
    nextWeekOverDue: groupByMilestoneName(overDueNextWeekTasks),
    noDueDate: noDueDateTasks,
    noDueDateCount: noDueDateCount,
  };
};

/**
 * Categorizes tasks in a single pass through the array for better performance
 */
const categorizeTasksInSinglePass = (
  tasks: MilestoneTask[],
  filteredHomeSiteIds: Set<string>,
  taskOrder: Record<string, number>,
  today: dayjs.Dayjs,
  startOfThisWeek: dayjs.Dayjs,
  endOfThisWeek: dayjs.Dayjs,
  startOfNextWeek: dayjs.Dayjs,
  endOfNextWeek: dayjs.Dayjs
) => {
  if ((filteredHomeSiteIds.size === 0) || (tasks.length === 0)) {
    return {
      thisWeekTasks: [],
      nextWeekTasks: [],
      futureWeekTasks: [],
      overDueThisWeekTasks: []
    };
  }

  const thisWeekGroups = new Map<string, MilestoneTask>();
  const nextWeekGroups = new Map<string, MilestoneTask>();
  const futureWeekGroups = new Map<string, MilestoneTask>();
  const overDueGroups = new Map<string, MilestoneTask>();

  for (const task of tasks) {
    if (!filteredHomeSiteIds.has(task.homeSiteId)) continue;
    if (task.status === false || task.completionDate != null) continue;
    if (task.dueDate == null) continue;

    const dueDate = dayjs(task.dueDate);
    if (!dueDate.isValid()) continue;

    const groupKey = `${task.homeSiteId}-${task.mileStoneName}`;
    const shouldReplace = (current: MilestoneTask | undefined) => 
      !current || shouldReplaceTask(current, task, taskOrder);

    // Check if overdue (before today) - can be in both overdue and time-based categories
    const isOverdue = dueDate.isBefore(today);
    if (isOverdue) {
      if (shouldReplace(overDueGroups.get(groupKey))) {
        overDueGroups.set(groupKey, task);
      }
    }

    // Check if this week (includes overdue tasks that are still within this week)
    // Original filterTasks(null, endOfThisWeek) means any date up to end of this week
    if (dueDate.isSameOrBefore(endOfThisWeek)) {
      if (shouldReplace(thisWeekGroups.get(groupKey))) {
        thisWeekGroups.set(groupKey, task);
      }
    }
    // Check if next week
    else if (dueDate.isSameOrAfter(startOfNextWeek) && dueDate.isSameOrBefore(endOfNextWeek)) {
      if (shouldReplace(nextWeekGroups.get(groupKey))) {
        nextWeekGroups.set(groupKey, task);
      }
    }
    // Check if future (after next week)
    else if (dueDate.isAfter(endOfNextWeek)) {
      if (shouldReplace(futureWeekGroups.get(groupKey))) {
        futureWeekGroups.set(groupKey, task);
      }
    }
  }

  return {
    thisWeekTasks: Array.from(thisWeekGroups.values()),
    nextWeekTasks: Array.from(nextWeekGroups.values()),
    futureWeekTasks: Array.from(futureWeekGroups.values()),
    overDueThisWeekTasks: Array.from(overDueGroups.values())
  };
};

const filterTasks = (start: dayjs.Dayjs | null, end: dayjs.Dayjs | null, tasks: MilestoneTask[], filteredHomeSiteIds: Set<string>, taskOrder: Record<string, number>) => {
  if ((filteredHomeSiteIds.size === 0) || (tasks.length === 0)) {
    return [];
  }
  return filterAndGroupTasks(tasks, filteredHomeSiteIds, (task) => {
    const dueDate = dayjs(task.dueDate);
    return (
      task.dueDate != null &&
      !(task.status === false) &&
      task.completionDate == null &&
      (start === null || dueDate.isSameOrAfter(start)) &&
      (end === null || dueDate.isSameOrBefore(end))
    );
  }, taskOrder);
};

/**
 * Filters overdue tasks and selects the earliest dueDate task per (homeSiteId, milestoneName).
 */
const filterTasksOverDue = (today: dayjs.Dayjs, tasks: MilestoneTask[], filteredHomeSiteIds: Set<string>, taskOrder: Record<string, number>) => {
  return filterAndGroupTasks(tasks, filteredHomeSiteIds, (task) => {
    return (
      task.dueDate != null &&
      (task.status === null || task.status) &&
      task.completionDate == null &&
      dayjs(task.dueDate).isBefore(today)
    );
  }, taskOrder);
};

const groupByMilestoneName = (tasks: MilestoneTask[]) => {
  return tasks.reduce((grouped: Record<string, MilestoneTask[]>, task: MilestoneTask) => {
    const milestoneName = task.mileStoneName;
    if (!grouped[milestoneName]) {
      grouped[milestoneName] = [];
    }
    grouped[milestoneName].push(task);
    return grouped;
  }, {});
};


/**
 * Filters and groups tasks based on the provided predicate function and home site IDs.
 *
 * @param tasks - An array of `MilestoneTask` objects to be filtered and grouped.
 * @param filteredHomeSiteIds - A set of home site IDs to filter the tasks.
 * @param predicateFn - A predicate function to determine if a task should be included.
 * @returns An array of grouped `MilestoneTask` objects.
 *
 * @remarks
 * - The function iterates over each task and checks if the task's `homeSiteId` is in the `filteredHomeSiteIds` set and if it satisfies the `predicateFn`.
 * - If both conditions are met, it generates a group key using the `homeSiteId` and `mileStoneName`.
 * - It then checks if the group key already exists in the `taskGroups` map.
 * - If the group key does not exist or the current task should replace the existing task (based on `shouldReplaceTask`), it updates the map with the new task.
 * - Finally, it returns an array of the grouped tasks.
 */
const filterAndGroupTasks = (tasks: MilestoneTask[], filteredHomeSiteIds: Set<string>, predicateFn: (task: MilestoneTask) => boolean, taskOrder: Record<string, number>) => {
  const taskGroups = new Map<string, MilestoneTask>();
  tasks.forEach((task) => {
    if (!filteredHomeSiteIds.has(task.homeSiteId) || !predicateFn(task)) return;
    const groupKey = `${task.homeSiteId}-${task.mileStoneName}`;
    const currentTask = taskGroups.get(groupKey);
    if (!currentTask || shouldReplaceTask(currentTask, task, taskOrder)) {
      taskGroups.set(groupKey, task);
    }
  });
  return Array.from(taskGroups.values());
};

/**
 * Determines whether a new task should replace the current task based on their due dates and update dates.
 *
 * @param currentTask - The current task being compared.
 * @param newTask - The new task being evaluated for replacement.
 * @returns A boolean indicating whether the new task should replace the current task.
 *
 * The new task will replace the current task if:
 * - The new task's due date is before the current task's due date.
 * - The new task's due date is the same as the current task's due date, but the new is before the current task in order.
 */
const shouldReplaceTask = (currentTask: MilestoneTask, newTask: MilestoneTask, taskOrder: Record<string, number>) => {
  if (!currentTask.dueDate || !newTask.dueDate) {
    // at least one of them is null
    if (!currentTask.dueDate && !newTask.dueDate) {
      return dayjs(newTask.createdOnDate).isBefore(dayjs(currentTask.createdOnDate));
    }
    return !currentTask.dueDate ? true : false;
  }
  const currentDueDate = dayjs(currentTask.dueDate);
  const newDueDate = dayjs(newTask.dueDate);
  return (
    newDueDate.isBefore(currentDueDate) ||
    (newDueDate.isSame(currentDueDate) && (taskOrder[newTask.mileStoneName + newTask.taskName] < taskOrder[currentTask.mileStoneName + currentTask.taskName]))
  );
};

const newMilestoneTasks = (hsId: string, milestone: DivisionMileStone): MilestoneTask[] => {
  const msTasks: MilestoneTask[] = [];
  const msTask: MilestoneTask = {
    id: '',
    homeSiteId: hsId,
    mileStoneName: milestone.mileStoneName,
    taskName: milestone.taskName,
    dueDate: null,
    completionDate: null,
    status: null,
    saleDate: null,
    closeOfEscrowEstimated: null,
    closeOfEscrowActual: null,
    createdOnDate: new Date(),
    updatedOnDate: new Date(),
    divisionCode: "",
    conciergeEmail: "",
    createdBy: "",
    updatedBy: "",
    taskCode: milestone.taskCode,
  };
  msTasks.push(msTask);
  return msTasks;
};

const newMileStoneMap = (hsId: string, divMilestones: DivisionMileStones): Map<string, MilestoneTask[]> => {
  const milestones = new Map<string, MilestoneTask[]>();
  for (const milestone of divMilestones.data) {
    // Only include milestones that are active (status = true)
    if (milestone.status === true) {
      const existingTasks = milestones.get(milestone.mileStoneName) || [];
      existingTasks.push(...newMilestoneTasks(hsId, milestone));
      milestones.set(milestone.mileStoneName, existingTasks);
    }
  }
  return milestones;
};

const noTaskDueHomeSites = (divMilestones: DivisionMileStones, tasks: MilestoneTask[], filteredHomeSiteIds: Set<string>, activeMilestoneNames: Set<string>): Map<string, Map<string, MilestoneTask>> => {
  const homesites = new Map<string, Map<string, MilestoneTask[]>>();
  for (const hsId of filteredHomeSiteIds) {
    homesites.set(hsId, newMileStoneMap(hsId, divMilestones));
  }

  for (const task of tasks) {
    const tHS = homesites.get(task.homeSiteId);
    if (tHS) {
      if (!task.completionDate && !(task.status === false) && task.dueDate) {
        tHS.delete(task.mileStoneName);
      } else if (task.completionDate != null || task.status === false) {
        const existingTasks = tHS.get(task.mileStoneName);
        if (existingTasks) {
          const taskIndex = existingTasks.findIndex(t => t.taskName.toLowerCase() === task.taskName.toLowerCase());
          if (taskIndex !== -1) {
            existingTasks.splice(taskIndex, 1);
          }
        }
      }
    }
  }
  const results = new Map<string, Map<string, MilestoneTask>>();
  homesites.forEach((milestoneTasks, hskey, hsmap) => {
    milestoneTasks.forEach((tasks, key, map) => {
      // Only include milestones that are active (status = true)
      if (activeMilestoneNames.has(key) && tasks.length > 0) {
        results.set(hskey, results.get(hskey) || new Map());
        results.get(hskey)?.set(key, tasks[0]);
      }
    });
  });
  return results;
};

const getTimeFrame = (metrics: MilestoneMetrics, timeFrame: AlignmentType) => {
  if (timeFrame === THIS_WEEK) {
    return { homesites: metrics.thisWeek, homesitesOverDue: metrics.thisWeekOverDue };
  } else if (timeFrame === NEXT_WEEK) {
    return { homesites: metrics.nextWeek, homesitesOverDue: null };
  } else if (timeFrame === FUTURE_WEEKS) {
    return { homesites: metrics.futureWeek, homesitesOverDue: null };
  }
  return {};
};

export const getHomesiteMilestoneRows = (homesites: HomeSitesDataIndex[], metrics: MilestoneMetrics, timeFrame: AlignmentType, selectedDropdownMilestone: string, notes: Notes, exceptions: Exceptions, alerts: Alerts, divisionMilestonesData?: DivisionMileStones) => {
  const { homesites: homesitesMetrics } = getTimeFrame(metrics, timeFrame);

  // Filter division milestones to only include those with status = true
  const activeDivisionMilestones = divisionMilestonesData?.data.filter(milestone => milestone.status === true && milestone.automated !== true) || [];
  const activeMilestoneNames = new Set(activeDivisionMilestones.map(milestone => milestone.mileStoneName));

  if (selectedDropdownMilestone === ALL_MILESTONES) {
    return getAllHomesites(homesites, homesitesMetrics || {}, notes, exceptions, alerts, activeMilestoneNames);
  }
  if (selectedDropdownMilestone === NO_DUE_DATE) {
    return getNoDueDateHomesites(homesites, metrics.noDueDate, notes, exceptions, alerts, activeMilestoneNames);
  }
  return getMilestoneHomesites(homesites, (homesitesMetrics || {})[selectedDropdownMilestone], notes, exceptions, alerts, activeMilestoneNames);
}

const getAllHomesites = (homesites: HomeSitesDataIndex[], metrics: Record<string, MilestoneTask[]>, notes: Notes, exceptions: Exceptions, alerts: Alerts, activeMilestoneNames: Set<string>): ITaskHomesite[] => {
  const results: ITaskHomesite[] = [];
  Object.keys(metrics).forEach(element => {
    // Only include milestones that are active (status = true)
    if (activeMilestoneNames.has(element)) {
      results.push(...getMilestoneHomesites(homesites, metrics[element], notes, exceptions, alerts, activeMilestoneNames));
    }
  });
  return results;
};

const getNoDueDateHomesites = (homesites: HomeSitesDataIndex[], metrics: Map<string, Map<string, MilestoneTask>>, notes: Notes, exceptions: Exceptions, alerts: Alerts, activeMilestoneNames: Set<string>): ITaskHomesite[] => {
  const results: ITaskHomesite[] = [];
  
  // Build lookup maps for notes, exceptions, and alerts - single pass
  const notesByHomeSiteId = new Map<string, number>();
  const exceptionsByHomeSiteId = new Map<string, number>();
  const alertsByHomeSiteId = new Map<string, number>();
  
  for (const note of notes.data) {
    if (note.status === true) {
      notesByHomeSiteId.set(note.homeSiteId, (notesByHomeSiteId.get(note.homeSiteId) || 0) + 1);
    }
  }
  
  for (const exception of exceptions.data) {
    exceptionsByHomeSiteId.set(exception.homeSiteId, (exceptionsByHomeSiteId.get(exception.homeSiteId) || 0) + 1);
  }
  
  for (const alert of alerts.data) {
    if (alert.status === ALERT_ACTIVE_STATUS) {
      alertsByHomeSiteId.set(alert.homeSiteId, (alertsByHomeSiteId.get(alert.homeSiteId) || 0) + 1);
    }
  }
  
  // Build homesites lookup map for faster access
  const homesitesMap = new Map<string, HomeSitesDataIndex>();
  for (const hs of homesites) {
    homesitesMap.set(hs.homeSiteId, hs);
  }
  
  metrics.forEach((milestoneTasks, hskey) => {
    const hs = homesitesMap.get(hskey);
    if (hs) {
      milestoneTasks.forEach((task) => {
        // Only include milestones that are active (status = true)
        if (activeMilestoneNames.has(task.mileStoneName)) {
          results.push({
            ...hs,
            homesiteId: hs.id,
            mileStoneName: task.mileStoneName,
            taskName: task.taskName,
            dueDate: null,
            id: `${hs.id}-${task.mileStoneName}`,
            concierge: hs.conciergeEmail,
            numNotes: notesByHomeSiteId.get(hs.id) ?? 0,
            numExceptions: exceptionsByHomeSiteId.get(hs.id) ?? 0,
            numAlerts: alertsByHomeSiteId.get(hs.homeSiteId) ?? 0
          });
        }
      });
    }
  });
  return results;
};

const getMilestoneHomesites = (homesites: HomeSitesDataIndex[], milestones: MilestoneTask[], notes: Notes, exceptions: Exceptions, alerts: Alerts, activeMilestoneNames: Set<string>): ITaskHomesite[] => {
  const results: ITaskHomesite[] = [];
  if (!milestones || milestones.length === 0) {
    return results;
  }
  
  // Build lookup maps for notes, exceptions, and alerts - single pass
  const notesByHomeSiteId = new Map<string, number>();
  const exceptionsByHomeSiteId = new Map<string, number>();
  const alertsByHomeSiteId = new Map<string, number>();
  
  for (const note of notes.data) {
    if (note.status === true) {
      notesByHomeSiteId.set(note.homeSiteId, (notesByHomeSiteId.get(note.homeSiteId) || 0) + 1);
    }
  }
  
  for (const exception of exceptions.data) {
    if (exception.status === true) {
      exceptionsByHomeSiteId.set(exception.homeSiteId, (exceptionsByHomeSiteId.get(exception.homeSiteId) || 0) + 1);
    }
  }
  
  for (const alert of alerts.data) {
    if (alert.status === ALERT_ACTIVE_STATUS) {
      alertsByHomeSiteId.set(alert.homeSiteId, (alertsByHomeSiteId.get(alert.homeSiteId) || 0) + 1);
    }
  }
  
  // Build homesites lookup map for faster access
  const homesitesMap = new Map<string, HomeSitesDataIndex>();
  for (const hs of homesites) {
    homesitesMap.set(hs.id, hs);
  }
  
  for (const milestone of milestones) {
    // Only include milestones that are active (status = true)
    if (activeMilestoneNames.has(milestone.mileStoneName)) {
      const hs = homesitesMap.get(milestone.homeSiteId);
      if (hs) {
        results.push({
          ...hs,
          homesiteId: hs.id,
          mileStoneName: milestone.mileStoneName,
          taskName: milestone.taskName,
          dueDate: milestone.dueDate,
          id: `${hs.id}-${milestone.mileStoneName}`,
          concierge: hs.conciergeEmail,
          numNotes: notesByHomeSiteId.get(hs.id) ?? 0,
          numExceptions: exceptionsByHomeSiteId.get(hs.id) ?? 0,
          numAlerts: alertsByHomeSiteId.get(hs.homeSiteId) ?? 0
        });
      }
    }
  }
  return results;
};

export const getMilestoneCounts = (metrics: MilestoneMetrics, timeFrame: AlignmentType, divisionMilestonesData?: DivisionMileStones) => {
  if (!metrics) return {
    [ALL_MILESTONES]: { numHomesites: 0, numOverdue: 0 },
    [NO_DUE_DATE]: { numHomesites: 0, numOverdue: 0 }
  };

  const activeMilestoneNames = new Set(divisionMilestonesData?.data.filter(m => m.status && m.automated !== true).map(m => m.mileStoneName) || []);
  const { homesites, homesitesOverDue } = getTimeFrame(metrics, timeFrame);

  const counts: { [key: string]: MileStoneCount } = {
    [ALL_MILESTONES]: { numHomesites: 0, numOverdue: 0 },
    [NO_DUE_DATE]: { numHomesites: metrics.noDueDateCount, numOverdue: 0 }
  };

  homesites && Object.entries(homesites).forEach(([name, tasks]) => {
    const isActive = activeMilestoneNames.has(name);
    const homesiteCount = isActive ? (tasks?.length || 0) : 0;
    const overdueCount = isActive && homesitesOverDue ? (homesitesOverDue[name]?.length || 0) : 0;

    counts[name] = { numHomesites: homesiteCount, numOverdue: overdueCount };
    if (isActive) {
      counts[ALL_MILESTONES].numHomesites += homesiteCount;
      counts[ALL_MILESTONES].numOverdue += overdueCount;
    }
  });

  return counts;
};
